import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { LEAVING_ROW_CLASSES, type TaskData } from "./TaskRow";

interface Props {
  child: TaskData;
  isEditing: boolean;
  draft: string;
  onDraftChange: (value: string) => void;
  onStartEdit: () => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onToggle: () => void;
  onDrill: () => void;
}

export default function SortableSubtaskRow({
  child,
  isEditing,
  draft,
  onDraftChange,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onToggle,
  onDrill,
}: Props) {
  const childDone = child.status === "completed";
  const grandCount = child.childrenCount ?? 0;

  // The id is namespaced so the same task rendered in the list tree and in the
  // open details panel doesn't collide in dnd-kit's registry.
  const sortable = useSortable({
    id: `detail:${child.id}`,
    disabled: isEditing || childDone || child.leaving,
    data: { kind: "detail-subtask", subtaskId: child.id },
  });

  const style = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
    opacity: sortable.isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={sortable.setNodeRef}
      style={style}
      {...sortable.attributes}
      {...sortable.listeners}
      className={`subtask-row ${sortable.isDragging ? "cursor-grabbing" : "cursor-grab"}${child.leaving ? ` ${LEAVING_ROW_CLASSES}` : ""}`}
    >
      <button
        type="button"
        aria-label={childDone ? "Вернуть в активные" : "Завершить подзадачу"}
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        className={`task-checkbox ${childDone ? "task-checkbox-done" : ""}`}
      >
        {childDone && (
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        )}
      </button>
      {isEditing ? (
        <input
          type="text"
          autoFocus
          value={draft}
          onChange={(e) => onDraftChange(e.target.value)}
          onBlur={onSaveEdit}
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onSaveEdit();
            } else if (e.key === "Escape") {
              onCancelEdit();
            }
          }}
          className="subtask-title-input"
        />
      ) : (
        <span
          className={`subtask-title ${childDone ? "task-row-done" : ""}`}
          onClick={onStartEdit}
        >
          {child.title}
        </span>
      )}
      {grandCount > 0 && <span className="subtask-count">{grandCount}</span>}
      <button
        type="button"
        aria-label={`Открыть ${child.title}`}
        onClick={onDrill}
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        className="subtask-drill"
      >
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  );
}
